import cx from 'classnames';
import { memo } from 'react';

import { isNullable } from '@flipster/utils/nullable';
import { BigNumber, NumberFormatterOptions, numberFormatter } from '@flipster/utils/num';

import { NumberFontType } from '../types/base';
import styles from './price.module.scss';

export type PriceProps = {
  fontType?: NumberFontType;
  abs?: boolean;
  price?: BigNumber | number | string | null;
  className?: string;
  currencyClassName?: string;
  dollarSign?: boolean;
  currency?: string;
  withPlus?: boolean;
  withColor?: boolean;
  priceColor?: 'green' | 'red';
  // text options
  zeroString?: string;
} & NumberFormatterOptions;

/**
 * ticker/reference price를 표시할 때는 반올림(round-half-up) 처리해야함.
 * context
 * - https://prextech.slack.com/archives/C04407SABK4/p1699347178947029?thread_ts=1699345769.485309&cid=C04407SABK4
 * - https://prextech.slack.com/archives/C081Z1PECA2/p1744027471697389?thread_ts=1744018883.147529&cid=C081Z1PECA2
 */
export const Price = memo(
  ({
    fontType = 'num2',
    method = 'round-down',
    abs = false,
    price,
    currency,
    className,
    currencyClassName,
    zeroString,
    dollarSign = false,
    withPlus = false,
    withColor,
    priceColor,
    precision,
    withComma = true,
    toFixed = false,
  }: PriceProps) => {
    if (isNullable(price)) {
      return <span className={cx(styles.wrapper, styles[fontType], className)}>-</span>;
    }

    if (Number(price) === 0) {
      let zeroFormat = zeroString ?? '0';
      if (toFixed && !zeroString) zeroFormat = Number(0).toFixed(precision);

      return (
        <span className={cx(styles.wrapper, styles[fontType], className)} translate='no'>
          <span>{dollarSign && '$'}</span>
          <span>{zeroFormat}</span>
          {currency && <span className={cx(styles.currency, currencyClassName)}> {currency}</span>}
        </span>
      );
    }

    const numberPrice = Number(price);
    const stringPrice = price.toString().replace('-', '');
    const showNumber = numberFormatter(stringPrice, { precision, withComma, method, toFixed });
    const isZeroShowNumber = Number(showNumber) === 0;

    return (
      <span className={cx(styles.wrapper, styles[fontType], className)} translate='no'>
        <span
          className={cx(styles.price, {
            [styles.green]: !isZeroShowNumber && withColor && (priceColor === 'green' || Number(numberPrice) > 0),
            [styles.red]: !isZeroShowNumber && withColor && (priceColor === 'red' || Number(numberPrice) < 0),
            [styles.plus]: !isZeroShowNumber && withPlus && numberPrice > 0,
            [styles.minus]: !isZeroShowNumber && !abs && numberPrice < 0,
          })}
        >
          {dollarSign && <span>$</span>}
          <span>{showNumber}</span>
        </span>
        {currency && <span className={cx(styles.currency, currencyClassName)}> {currency}</span>}
      </span>
    );
  },
);
